import * as React from 'react'
import { cn } from '@/lib/utils'

interface CollapsibleProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function Collapsible({ open, onOpenChange, className, children, ...props }: CollapsibleProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const isOpen = open ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen

  return (
    <div className={cn(className)} data-state={isOpen ? 'open' : 'closed'} {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === CollapsibleTrigger) {
            return React.cloneElement(child as React.ReactElement<CollapsibleTriggerInternalProps>, {
              _isOpen: isOpen,
              _onToggle: () => setOpen(!isOpen),
            })
          }
          if (child.type === CollapsibleContent) {
            return React.cloneElement(child as React.ReactElement<CollapsibleContentInternalProps>, {
              _isOpen: isOpen,
            })
          }
        }
        return child
      })}
    </div>
  )
}

interface CollapsibleTriggerInternalProps extends React.HTMLAttributes<HTMLDivElement> {
  _isOpen?: boolean
  _onToggle?: () => void
  asChild?: boolean
}

function CollapsibleTrigger({
  _isOpen,
  _onToggle,
  asChild,
  children,
  className,
  ...props
}: CollapsibleTriggerInternalProps) {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      onClick: (e: React.MouseEvent) => {
        _onToggle?.()
        const childProps = children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>
        childProps.props.onClick?.(e)
      },
      'data-state': _isOpen ? 'open' : 'closed',
    })
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={_onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          _onToggle?.()
        }
      }}
      data-state={_isOpen ? 'open' : 'closed'}
      className={cn('cursor-pointer', className)}
      {...props}
    >
      {children}
    </div>
  )
}

interface CollapsibleContentInternalProps extends React.HTMLAttributes<HTMLDivElement> {
  _isOpen?: boolean
}

function CollapsibleContent({ _isOpen, className, children, ...props }: CollapsibleContentInternalProps) {
  if (!_isOpen) return null

  return (
    <div
      data-state={_isOpen ? 'open' : 'closed'}
      className={cn('overflow-hidden', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent }
